/**
 * The BFF's /api/auth/me for AuthContext (#847, #1155): the persona roles it
 * enforces in the acting org, the org role, whether the account has no
 * org, and the org it acts in (#1149). Rejects on a network error, a non-OK answer, a body without `roles`,
 * or no answer within `timeoutMs`, so a hung BFF can never leave the role
 * landing waiting forever.
 */
import { filterKnownRoles, isRole } from '@jeket-cloud/types/auth';
import type { Role } from '@jeket-cloud/types/auth';

export const AUTH_ME_TIMEOUT_MS = 8000;

export interface AuthMe {
  roles: Role[];
  orgRole: Role | null;
  noOrganization: boolean;
  /**
   * The org the BFF acts in for this session (#1149): the X-User-Org it
   * forwards to services, from the answer's `orgId`. null when it forwards
   * none (a superuser with no pick, staff in no org, a member of several orgs
   * who has not picked one).
   */
  actingOrgId: string | null;
  /**
   * The BFF will not act in this request's X-Org-Override (#1171): a
   * non-superuser's pick outside their orgs, or a superuser's of an org that
   * no longer exists.
   */
  orgOverrideRejected: boolean;
}

export async function fetchAuthMe(
  token: string,
  orgOverride: string | null,
  { timeoutMs = AUTH_ME_TIMEOUT_MS, signal }: { timeoutMs?: number; signal?: AbortSignal } = {},
): Promise<AuthMe> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new Error('/api/auth/me timed out')), timeoutMs);
  const onAbort = () => controller.abort(signal?.reason);
  signal?.addEventListener('abort', onAbort);
  try {
    const res = await fetch('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}`, ...(orgOverride ? { 'X-Org-Override': orgOverride } : {}) },
      signal: controller.signal,
    });
    if (!res.ok) throw new Error(`/api/auth/me answered ${res.status}`);
    const body = await res.json();
    const roles = body?.user?.roles;
    if (!Array.isArray(roles)) throw new Error('/api/auth/me answered no roles');
    const orgRole = body.user.orgRole;
    return {
      roles: filterKnownRoles(roles),
      orgRole: isRole(orgRole) ? orgRole : null,
      noOrganization: body.user.noOrganization === true,
      actingOrgId: typeof body.user.orgId === 'string' && body.user.orgId !== '' ? body.user.orgId : null,
      orgOverrideRejected: body.user.orgOverrideRejected === true,
    };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', onAbort);
  }
}
